// Form rules, labels and summaries for the IT apps register (spec §4.2).
// Pure functions so the drawer and the tab share one source of truth.

export const COMPANY_ORDER = ['robusta', 'octopus', 'coworker', 'others'];

export const LABELS = {
  company: { robusta: 'Robusta', octopus: 'Octopus', coworker: 'Coworker', others: 'Others' },
  it_role: { admin: 'IT runs it', oversight: 'IT oversight', other_owner: 'Another owner runs it' },
  sign_in: {
    google_sso: 'Google sign-in', own_login: 'Own login',
    identity_provider: 'Identity provider', unconfirmed: 'Not confirmed',
  },
  billing: { per_user: 'Per user', flat: 'Flat', no_cost: 'No cost', unconfirmed: 'Not confirmed' },
  billed_by: { rtg: 'RTG', partner: 'Partner', unconfirmed: 'Not confirmed' },
};

export const MAX_LINES = 20;

// Mirrors the DB check `key ~ '^[a-z0-9_-]{2,40}$'`.
const KEY_RE = /^[a-z0-9_-]{2,40}$/;
// Google Workspace is the identity provider itself; the server forces this too.
const GOOGLE_KEY = 'google';

export const MSG = {
  name: 'Add a name.',
  close_action: 'Add a close action.',
  offboarding_steps: 'Add at least one offboarding step.',
  partner_name: 'Add the partner who bills this app.',
  key: 'Use lowercase letters, numbers, - or _.',
  duplicate_key: 'Another app already uses this key.',
  too_long: 'This is too long.',
  invalid: 'Pick a valid option.',
  billed_by_not_allowed: 'No cost apps are not billed.',
};

const text = (v) => (typeof v === 'string' ? v.trim() : '');
const lines = (v) => (Array.isArray(v) ? v.map(text).filter(Boolean) : []);

export function blankApp() {
  return {
    key: '', name: '', company: 'robusta', it_role: 'admin', tool_owner: '', it_owner: '',
    admin_console: '', sign_in: 'unconfirmed',
    billing: 'unconfirmed', billed_by: 'unconfirmed', partner_name: '', close_action: '',
    offboarding_steps: [''], seat_note: '', what_it_covers: [], notes: '', tool_id: null,
  };
}

/** Which conditional fields the form shows. */
export function visibleFields(form) {
  const noCost = form.billing === 'no_cost';
  return {
    billedBy: !noCost,
    notBilled: noCost,
    partnerName: !noCost && form.billed_by === 'partner',
    signInLocked: form.key === GOOGLE_KEY,
  };
}

/** Client-side validation before submit: `{ field: message }`, empty when valid. */
export function validateForm(form, { isCreate = false } = {}) {
  const errors = {};
  if (isCreate && !KEY_RE.test(form.key || '')) errors.key = MSG.key;
  if (!text(form.name)) errors.name = MSG.name;
  if (!text(form.close_action)) errors.close_action = MSG.close_action;
  if (lines(form.offboarding_steps).length === 0) errors.offboarding_steps = MSG.offboarding_steps;
  if (visibleFields(form).partnerName && !text(form.partner_name)) errors.partner_name = MSG.partner_name;
  return errors;
}

/** Section header numbers. Retired apps are listed but not counted. */
export function sectionStats(apps) {
  const active = apps.filter((a) => a.active !== false);
  return {
    count: active.length,
    perUser: active.filter((a) => a.billing === 'per_user').length,
  };
}

export function sectionSummary({ count, perUser }) {
  return `${count} ${count === 1 ? 'app' : 'apps'} · ${perUser} per user`;
}

const nullable = (v) => text(v) || null;

/** The write body: allowlisted fields only, conditional fields cleared. */
export function toPayload(form, { isCreate = false } = {}) {
  const v = visibleFields(form);
  const body = {
    name: text(form.name),
    company: form.company,
    it_role: form.it_role,
    tool_owner: nullable(form.tool_owner),
    it_owner: nullable(form.it_owner),
    admin_console: nullable(form.admin_console),
    sign_in: v.signInLocked ? 'identity_provider' : form.sign_in,
    billing: form.billing,
    billed_by: v.billedBy ? form.billed_by : null,
    partner_name: v.partnerName ? nullable(form.partner_name) : null,
    close_action: text(form.close_action),
    offboarding_steps: lines(form.offboarding_steps),
    seat_note: nullable(form.seat_note),
    what_it_covers: lines(form.what_it_covers),
    notes: nullable(form.notes),
    // Always sent: the drawer's catalog field owns it, so Save never drops a link by omission.
    tool_id: Number.isInteger(form.tool_id) ? form.tool_id : null,
  };
  return isCreate ? { key: text(form.key), ...body } : body;
}

const BARE_DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i;

/** A link target for the admin console, or null to render it as plain text. */
export function adminConsoleHref(value) {
  const v = text(value);
  if (!v) return null;
  if (/^https:\/\//i.test(v)) {
    try {
      const url = new URL(v);
      return url.protocol === 'https:' ? v : null;
    } catch {
      return null;
    }
  }
  return BARE_DOMAIN_RE.test(v) ? `https://${v}` : null;
}

// Field-specific message for a server `required` code.
const REQUIRED_BY_FIELD = {
  name: MSG.name, close_action: MSG.close_action, offboarding_steps: MSG.offboarding_steps,
  partner_name: MSG.partner_name, key: MSG.key,
};

/** Map the server's `{ fields: { field: code } }` to inline messages. */
export function serverFieldErrors(fields) {
  const out = {};
  Object.entries(fields || {}).forEach(([field, code]) => {
    if (code === 'required') out[field] = REQUIRED_BY_FIELD[field] || 'This is required.';
    else if (code === 'partner_required') out[field] = MSG.partner_name;
    else if (code === 'too_long') out[field] = MSG.too_long;
    else if (code === 'billed_by_not_allowed') out[field] = MSG.billed_by_not_allowed;
    else if (code === 'invalid') out[field] = field === 'key' ? MSG.key : MSG.invalid;
    else out[field] = MSG.invalid;
  });
  return out;
}
